import { useState, type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Loader2, X } from "lucide-react";
import { motivoSchema } from "@bo/shared";
import type { ErrorInterpretado } from "@/lib/webchat/errores.js";
import { AvisoError } from "./Avisos.js";
import { Campo } from "./Campo.js";

/** Diálogo modal accesible (foco atrapado, Escape cierra, título y descripción asociados). */
export function Modal({
  abierto,
  onCerrar,
  titulo,
  descripcion,
  children,
}: Readonly<{ abierto: boolean; onCerrar: () => void; titulo: string; descripcion?: ReactNode; children: ReactNode }>) {
  return (
    <Dialog.Root open={abierto} onOpenChange={(o) => !o && onCerrar()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-slate-950/50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-32px)] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-5 shadow-xl dark:bg-slate-900 max-h-[90vh] overflow-y-auto">
          <div className="flex items-start justify-between gap-4">
            <Dialog.Title className="text-lg font-semibold">{titulo}</Dialog.Title>
            <Dialog.Close className="btn-ghost p-1.5" aria-label="Cerrar">
              <X size={16} aria-hidden="true" />
            </Dialog.Close>
          </div>
          {descripcion ? (
            <Dialog.Description className="mt-1 text-sm text-slate-600 dark:text-slate-300">{descripcion}</Dialog.Description>
          ) : (
            <Dialog.Description className="sr-only">{titulo}</Dialog.Description>
          )}
          <div className="mt-4">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Pide un motivo (queda en la auditoría) antes de una acción: suspender, reactivar, revertir. */
export function DialogoMotivo({
  abierto,
  onCerrar,
  titulo,
  descripcion,
  accion,
  peligroso,
  ocupado,
  error,
  onConfirmar,
}: Readonly<{
  abierto: boolean;
  onCerrar: () => void;
  titulo: string;
  descripcion: ReactNode;
  accion: string;
  peligroso?: boolean;
  ocupado: boolean;
  error: ErrorInterpretado | null;
  onConfirmar: (motivo: string) => void;
}>) {
  const [motivo, setMotivo] = useState("");
  const [tocado, setTocado] = useState(false);
  const r = motivoSchema.safeParse(motivo);
  const errorMotivo = tocado && !r.success ? r.error.issues[0]?.message : undefined;
  return (
    <Modal abierto={abierto} onCerrar={onCerrar} titulo={titulo} descripcion={descripcion}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          setTocado(true);
          if (r.success) onConfirmar(r.data);
        }}
      >
        <Campo etiqueta="Motivo" obligatorio error={errorMotivo} ayuda="Queda en la auditoría. De 3 a 500 caracteres.">
          <textarea className="input min-h-[80px]" maxLength={500} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </Campo>
        <AvisoError error={error} />
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onCerrar}>
            Cancelar
          </button>
          <button type="submit" className={peligroso ? "btn-danger" : "btn-primary"} disabled={ocupado}>
            {ocupado && <Loader2 size={14} className="mr-1 animate-spin" aria-hidden="true" />}
            {accion}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** La baja: hay que escribir el host exacto y el motivo. */
export function DialogoBaja({
  abierto,
  onCerrar,
  host,
  ocupado,
  error,
  onConfirmar,
}: Readonly<{
  abierto: boolean;
  onCerrar: () => void;
  host: string;
  ocupado: boolean;
  error: ErrorInterpretado | null;
  onConfirmar: (motivo: string, confirmarHost: string) => void;
}>) {
  const [motivo, setMotivo] = useState("");
  const [confirmacion, setConfirmacion] = useState("");
  const [tocado, setTocado] = useState(false);
  const r = motivoSchema.safeParse(motivo);
  const coincide = confirmacion.trim().toLowerCase() === host;
  return (
    <Modal
      abierto={abierto}
      onCerrar={onCerrar}
      titulo="Dar de baja el webchat"
      descripcion={
        <>
          El sitio deja de responder (404), se apaga el bot y se quitan el registro DNS y el host de la regla de Cloudflare. El canal y
          su historial quedan en la base.
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          setTocado(true);
          if (r.success && coincide) onConfirmar(r.data, confirmacion.trim().toLowerCase());
        }}
      >
        <Campo
          etiqueta={
            <>
              Escribí <span className="mono">{host}</span> para confirmar
            </>
          }
          obligatorio
          error={tocado && !coincide ? "No coincide con el host del webchat" : (error?.campos.confirmarHost ?? undefined)}
        >
          <input
            className="input mono"
            value={confirmacion}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => setConfirmacion(e.target.value)}
          />
        </Campo>
        <Campo etiqueta="Motivo" obligatorio error={tocado && !r.success ? r.error.issues[0]?.message : undefined} ayuda="Queda en la auditoría.">
          <textarea className="input min-h-[80px]" maxLength={500} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </Campo>
        <AvisoError error={error} />
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onCerrar}>
            Cancelar
          </button>
          <button type="submit" className="btn-danger" disabled={ocupado || !coincide}>
            {ocupado && <Loader2 size={14} className="mr-1 animate-spin" aria-hidden="true" />}
            Dar de baja
          </button>
        </div>
      </form>
    </Modal>
  );
}
